import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaDiscord, FaHashtag, FaGamepad, FaUsers } from "react-icons/fa";
import { FiSend, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import AnimateHeight from "react-animate-height";
import { handleDiscordAuthenticate } from "@/hooks/useDiscordGO";
import { useDiscordChat } from "@/hooks/useDiscordChat";
import { Token } from "@devour/client";
import "./DevourPlayChat.scss";

enum ChatChannel {
    GENERAL = "general",
    GAMING = "gaming",
    GUILDS = "guilds-chat",
}

interface Message {
    id: string;
    channelId: string;
    author: {
        id: string;
        username: string;
        avatar?: string;
        discriminator?: string;
    };
    content: string;
    timestamp: string;
    isSystem?: boolean;
}

interface DevourPlayChatProps {
    fullToken?: Token;
    discordLinked?: boolean;
    currentUser?: any;
    onLinkDiscord?: () => void;
}

/*
 * DevourPlay Discord Server Channel IDs (from actual Discord server)
 * Use development server and channels for local testing and develop-mirror5 environment
 */
const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "local" ||
                     process.env.NODE_ENV === "development" ||
                     window.location.hostname.includes("develop-mirror4") ||
                     window.location.hostname.includes("develop-mirror5") ||
                     window.location.hostname === "localhost";
const DISCORD_SERVER_ID = isDevelopment ? "1288875493504057405" : "1288875438491435141";
const CHANNEL_IDS = isDevelopment ? {
    [ChatChannel.GENERAL]: "1288875494225219596", // development general chat
    [ChatChannel.GAMING]: "1288875494225219596", // using same channel for testing
    [ChatChannel.GUILDS]: "1288875494225219596", // using same channel for testing
} : {
    [ChatChannel.GENERAL]: "1288875438491435144",
    [ChatChannel.GAMING]: "1300481728410026074",
    [ChatChannel.GUILDS]: "1366886902367780944",
};

const DevourPlayChat: React.FC<DevourPlayChatProps> = ({
    fullToken,
    discordLinked = false,
    currentUser,
    onLinkDiscord,
}) => {
    // In development, always use GENERAL channel since we only have one test channel
    const [currentChannel, setCurrentChannel] = useState<ChatChannel>(ChatChannel.GENERAL);
    const [inputValue, setInputValue] = useState("");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    // Debug logging moved after variable declarations

    const { isExpanded, toggleCard } = useCockpitPreferences();
    const expanded = isExpanded("devourplay-chat", true);

    // Memoize channel IDs to prevent infinite re-renders
    const channelIds = useMemo(() => Object.values(CHANNEL_IDS), []);

    // Always call Discord WebSocket hook (hooks must be called unconditionally)
    const discordChat = useDiscordChat({
        channelIds,
        serverId: DISCORD_SERVER_ID,
    });

    // Use real Discord data when available, otherwise use mock data
    const isConnected = discordChat?.isConnected || false;
    const isLoading = discordChat?.isLoading ?? false;
    const messages = discordLinked && discordChat
        ? discordChat.getChannelMessages(CHANNEL_IDS[currentChannel]) || []
        : [];

    // Mock messages for development
    const mockMessages: Message[] = [
        {
            id: "1",
            channelId: CHANNEL_IDS[ChatChannel.GENERAL],
            author: {
                id: "1",
                username: "TeamLead",
                avatar: "⚔️",
            },
            content: "Squad up for ranked? Need 2 more",
            timestamp: new Date(Date.now() - 120000).toISOString(),
        },
        {
            id: "2",
            channelId: CHANNEL_IDS[ChatChannel.GENERAL],
            author: {
                id: "system",
                username: "System",
                avatar: "🎯",
            },
            content: "Guild Mission progress: 68% complete! Keep pushing team!",
            timestamp: new Date(Date.now() - 300000).toISOString(),
            isSystem: true,
        },
        {
            id: "3",
            channelId: CHANNEL_IDS[ChatChannel.GENERAL],
            author: {
                id: "2",
                username: "ProSniper",
                avatar: "👤",
            },
            content: "I'm in! Let's get this W",
            timestamp: new Date(Date.now() - 60000).toISOString(),
        },
    ];

    // Filter messages by current channel
    const channelMessages = useMemo(() => {
    // Only show real Discord messages, no mock messages
        if (discordLinked && messages && Array.isArray(messages) && messages.length > 0) {
            // Messages from the hook are already filtered by channel
            return messages;
        }
        // Return empty array if no real messages
        return [];
    }, [messages || [], currentChannel, discordLinked]);

    // Component render debug logging removed - was too verbose for production

    // WebSocket connection is now handled by useDiscordChat hook

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        scrollToBottom();
    }, [channelMessages]);

    const scrollToBottom = () => {
    // Scroll within the messages container, not the entire page
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !discordLinked) return;

        if (discordChat && discordChat.canSendMessages) {
            try {
                await discordChat.sendMessage(CHANNEL_IDS[currentChannel], inputValue);
                setInputValue("");
            } catch (error) {
                console.error("Failed to send message:", error);
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTimestamp = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return "just now";
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
        return date.toLocaleDateString();
    };

    const processMentions = (content: string) => {
        if (!content) return content;

        // Replace <@userID> mentions with @username
        return content.replace(/<@!?(\d+)>/g, (match, userId) => {
            // Try to find the user in recent messages to get their username
            if (messages && Array.isArray(messages)) {
                for (const message of messages) {
                    if (message.author.id === userId) {
                        return `@${message.author.username}`;
                    }
                }
            }
            // Fallback to just @UserID if we can't find the username
            return `@${userId}`;
        });
    };

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard("devourplay-chat");
        }
    };

    const handleLinkDiscord = async () => {
        if (fullToken) {
            await handleDiscordAuthenticate(
                fullToken,
                // onSuccess - refresh user data and stay on current page
                () => {
                    // Force a page refresh to update user state, or use a more sophisticated state update
                    window.location.reload();
                },
                // onError - show error message
                (error) => {
                    console.error("[DevourPlayChat] Discord linking error:", error);
                    alert(`Discord linking failed: ${error}`);
                },
            );
        } else if (onLinkDiscord) {
            onLinkDiscord();
        }
    };

    const getChannelIcon = (channel: ChatChannel) => {
        switch (channel) {
            case ChatChannel.GENERAL:
                return <FaHashtag />;
            case ChatChannel.GAMING:
                return <FaGamepad />;
            case ChatChannel.GUILDS:
                return <FaUsers />;
        }
    };

    const getChannelName = (channel: ChatChannel) => {
        switch (channel) {
            case ChatChannel.GENERAL:
                return "General";
            case ChatChannel.GAMING:
                return "Gaming";
            case ChatChannel.GUILDS:
                return "Guilds Chat";
        }
    };

    return (
        <div className={`devourplay-chat ${expanded ? "expanded" : "collapsed"}`}>
            <div
                className="devourplay-chat__header"
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="devourplay-chat-content"
            >
                <h4>
                    <FaDiscord className="header-icon" />
                    DevourPlay Chat
                </h4>
                <button
                    className="devourplay-chat__collapse-toggle"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
            </div>

            <AnimateHeight
                id="devourplay-chat-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className="devourplay-chat__content">
                    {/* Channel Tabs */}
                    <div className="devourplay-chat__channels">
                        {isDevelopment
                        // In development, only show General tab since we're using one test channel
                            ? <button
                                key={ChatChannel.GENERAL}
                                className={"channel-tab active"}
                            >
                                {getChannelName(ChatChannel.GENERAL)} (Test)
                            </button>
                            :
                        // In production, show all channel tabs
                            Object.values(ChatChannel).map(channel =>
                                <button
                                    key={channel}
                                    className={`channel-tab ${currentChannel === channel ? "active" : ""}`}
                                    onClick={() => setCurrentChannel(channel)}
                                >
                                    {getChannelName(channel)}
                                </button>)
                        }
                    </div>

                    {/* Messages Area */}
                    <div className="devourplay-chat__messages" ref={messagesContainerRef}>
                        {!discordLinked
                            ? <div className="link-discord-prompt">
                                <FaDiscord className="discord-icon" />
                                <p>Link your Discord account to participate in chat</p>
                                <button onClick={handleLinkDiscord} className="link-button">
                                    {fullToken ? "Link Discord Account" : "SignUp & Link Account"}
                                </button>
                            </div>
                            : isLoading
                                ? <div className="loading-state">
                                    <p>Connecting to Discord...</p>
                                </div>
                                : channelMessages.length === 0
                                    ? <div className="empty-state">
                                        <p>No messages yet. Be the first to say something!</p>
                                    </div>
                                    : <>
                                        {channelMessages.map(message =>
                                            <div
                                                key={message.id}
                                                className={`message ${message.isSystem ? "system" : ""}`}
                                            >
                                                <div className="message-avatar">
                                                    {message.author.avatar
                                                        ? <img
                                                            src={`https://cdn.discordapp.com/avatars/${message.author.id}/${message.author.avatar}.png`}
                                                            alt={`${message.author.username}'s avatar`}
                                                            className="discord-avatar"
                                                        />
                                                        : <div className="default-avatar">
                                                            {message.author.username?.charAt(0)?.toUpperCase()}
                                                        </div>
                                                    }
                                                </div>
                                                <div className="message-content">
                                                    <div className="message-header">
                                                        <span className="message-author">{message.author.username}</span>
                                                        <span className="message-time">{formatTimestamp(message.timestamp)}</span>
                                                    </div>
                                                    <div className="message-text">{processMentions(message.content)}</div>

                                                    {/* Display attachments (images, GIFs, etc.) */}
                                                    {message.attachments && message.attachments.length > 0 &&
                        <div className="message-attachments">
                            {message.attachments.map(attachment => {
                                const isImage = attachment.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                return (
                                    <div key={attachment.id} className="attachment">
                                        {isImage
                                            ? <img
                                                src={attachment.url}
                                                alt={attachment.name}
                                                className="attachment-image"
                                                loading="lazy"
                                            />
                                            : <a
                                                href={attachment.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="attachment-link"
                                            >
                                                📎 {attachment.name}
                                            </a>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                                                    }

                                                    {/* Display embeds (Discord stickers, GIFs, rich content) */}
                                                    {message.embeds && message.embeds.length > 0 &&
                        <div className="message-embeds">
                            {message.embeds.map((embed, index) =>
                                <div key={index} className="embed">
                                    {/* Handle GIFs (gifv type) */}
                                    {embed.type === "gifv" && embed.video?.url &&
                                <video
                                    src={embed.video.url}
                                    poster={embed.thumbnail?.url}
                                    className="embed-gif"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                                    }
                                    {/* Handle regular images */}
                                    {embed.image?.url && embed.type !== "gifv" &&
                                <img
                                    src={embed.image.url}
                                    alt={embed.title || "Embed image"}
                                    className="embed-image"
                                    loading="lazy"
                                />
                                    }
                                    {/* Handle thumbnail-only embeds (like stickers without full image) */}
                                    {!embed.image?.url && !embed.video?.url && embed.thumbnail?.url &&
                                <img
                                    src={embed.thumbnail.url}
                                    alt={embed.title || "Thumbnail"}
                                    className="embed-thumbnail"
                                    loading="lazy"
                                />
                                    }
                                    {embed.title && <div className="embed-title">{embed.title}</div>}
                                    {embed.description && <div className="embed-description">{embed.description}</div>}
                                </div>)}
                        </div>
                                                    }

                                                    {/* Display Discord stickers */}
                                                    {message.stickers && message.stickers.length > 0 &&
                        <div className="message-stickers">
                            {message.stickers.map(sticker =>
                                <img
                                    key={sticker.id}
                                    src={sticker.url}
                                    alt={sticker.name}
                                    title={sticker.description || sticker.name}
                                    className="message-sticker"
                                    loading="lazy"
                                />)}
                        </div>
                                                    }
                                                </div>
                                            </div>)}
                                        <div ref={messagesEndRef} />
                                    </>
                        }
                    </div>

                    {/* Input Area */}
                    {discordLinked &&
            <div className="devourplay-chat__input">
                <input
                    type="text"
                    placeholder={`Message #${getChannelName(currentChannel).toLowerCase()}...`}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!isConnected && false} // Remove false when WebSocket is implemented
                />
                <button
                    className="send-button"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim()}
                >
                    <FiSend />
                </button>
            </div>
                    }
                </div>
            </AnimateHeight>
        </div>
    );
};

export default React.memo(DevourPlayChat);